/**
 * 3D 加载中：在建模锚点做粒子重组，替代实心橙框
 * 本地坐标：+X 东、+Y 北、+Z 上
 */
import * as THREE from 'three';

const CORE_COUNT = 560;
const SPARK_COUNT = 180;
const HEIGHT_CAP = 20;

const assembleSizeCache = {};

export function rememberAssembleSize(layerId, size) {
  if (layerId == null || !size) return;
  assembleSizeCache[String(layerId)] = {
    width: Number(size.width),
    depth: Number(size.depth),
    height: Number(size.height)
  };
}

export function peekAssembleSize(layerId) {
  if (layerId == null) return null;
  return assembleSizeCache[String(layerId)] || null;
}

/** 料子体量：平面跟模型宽深一致，高度跟模型走，最高 20 米 */
export function resolveLoadFxVolume(opts = {}) {
  const target = Number(opts.targetMeters);
  let w = Number(opts.width);
  let d = Number(opts.depth);
  if (!Number.isFinite(w) || w < 5) {
    w = Number.isFinite(target) && target > 8 ? target : 40;
  }
  if (!Number.isFinite(d) || d < 5) d = w;
  w = Math.min(400, Math.max(5, w));
  d = Math.min(400, Math.max(5, d));

  let h = Number(opts.height);
  if (!Number.isFinite(h) || h <= 0) {
    h = Math.min(HEIGHT_CAP, Math.max(6, Math.min(w, d) * 0.15));
  }
  h = Math.min(HEIGHT_CAP, Math.max(3, h));
  return { w, d, h };
}

export function inferModelHeight(sizeX, sizeY, sizeZ) {
  const sx = Number(sizeX);
  const sy = Number(sizeY);
  const sz = Number(sizeZ);
  const dims = [sx, sy, sz].filter((n) => Number.isFinite(n) && n > 0);
  if (!dims.length) return 12;
  const max = Math.max(...dims);
  const min = Math.min(...dims);
  if (Number.isFinite(sz) && sz > 0 && sz <= max * 0.85) return sz;
  if (min < max * 0.6) return min;
  return min;
}

function pickTarget(i, w, d, h) {
  const hw = w * 0.5;
  const hd = d * 0.5;
  const kind = i % 8;
  if (kind <= 2) {
    const e = i % 12;
    const t = ((i * 17) % 100) / 100;
    if (e < 4) return [ -hw + (e % 2) * w, -hd + (e < 2 ? 0 : d), t * h ];
    if (e < 8) return [ -hw + ((e - 4) % 2) * w, -hd + t * d, (e < 6 ? 0 : h) ];
    return [ -hw + t * w, -hd + ((e - 8) % 2) * d, (e < 10 ? 0 : h) ];
  }
  if (kind <= 4) {
    const gx = ((i * 3) % 7) / 6;
    const gy = ((i * 5) % 7) / 6;
    const gz = ((i * 11) % 6) / 5;
    return [ -hw + gx * w, -hd + gy * d, gz * h ];
  }
  return [
    (Math.random() - 0.5) * w,
    (Math.random() - 0.5) * d,
    Math.random() * h
  ];
}

function makeMat(color, opacity, size) {
  return new THREE.PointsMaterial({
    color,
    size,
    transparent: true,
    opacity,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
    toneMapped: false
  });
}

function fillCloud(count, w, d, h) {
  const positions = new Float32Array(count * 3);
  const seeds = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const target = pickTarget(i, w, d, h);
    const spread = 1.7 + Math.random() * 1.8;
    seeds[i] = {
      tx: target[0],
      ty: target[1],
      tz: target[2],
      sx: target[0] * spread + (Math.random() - 0.5) * w * 0.8,
      sy: target[1] * spread + (Math.random() - 0.5) * d * 0.8,
      sz: h * (0.15 + Math.random() * 1.4),
      phase: Math.random() * Math.PI * 2,
      spin: 0.55 + Math.random() * 1.1
    };
    positions[i * 3] = seeds[i].sx;
    positions[i * 3 + 1] = seeds[i].sy;
    positions[i * 3 + 2] = seeds[i].sz;
  }
  return { positions, seeds };
}

function writeCloud(positions, seeds, t, breathe) {
  for (let i = 0; i < seeds.length; i += 1) {
    const s = seeds[i];
    const assemble = THREE.MathUtils.clamp(breathe + Math.sin(t * 1.8 + s.phase) * 0.12, 0.22, 0.98);
    const swirl = t * s.spin;
    const cs = Math.cos(swirl);
    const sn = Math.sin(swirl);
    const rx = s.sx * cs - s.sy * sn;
    const ry = s.sx * sn + s.sy * cs;
    const rz = s.sz + Math.sin(t * 2.2 + s.phase) * 1.6;
    positions[i * 3] = rx + (s.tx - rx) * assemble;
    positions[i * 3 + 1] = ry + (s.ty - ry) * assemble;
    positions[i * 3 + 2] = rz + (s.tz - rz) * assemble;
  }
}

function makeHoloVolume(w, d, h) {
  const geo = new THREE.BoxGeometry(w, d, h);
  const edges = new THREE.EdgesGeometry(geo);
  geo.dispose();
  const line = new THREE.LineSegments(
    edges,
    new THREE.LineBasicMaterial({
      color: 0x66f0ff,
      transparent: true,
      opacity: 0.38,
      depthWrite: false,
      toneMapped: false
    })
  );
  line.position.z = h * 0.5;
  return line;
}

function makeRing(radius) {
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(radius, Math.max(0.12, radius * 0.012), 8, 72),
    new THREE.MeshBasicMaterial({
      color: 0x3d9bff,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false
    })
  );
  return ring;
}

function makeScan(w, d) {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w * 0.96, d * 0.96),
    new THREE.MeshBasicMaterial({
      color: 0x9be7ff,
      transparent: true,
      opacity: 0.16,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      toneMapped: false
    })
  );
  return mesh;
}

function setMarkerHidden(handle, hidden) {
  try {
    const el = handle.marker?.getElement?.();
    if (el) el.style.display = hidden ? 'none' : '';
  } catch (_) { /* ignore */ }
}

/**
 * 勾选瞬间：在锚点铺粒子重组，立刻标出将要落模的位置
 */
export function addLoadAssembleFx(handle, opts) {
  removeLoadAssembleFx(handle);
  const { w, d, h } = resolveLoadFxVolume(
    typeof opts === 'number' || opts == null ? { targetMeters: opts } : opts
  );
  const group = new THREE.Group();
  group.userData.zhxtPlaceholder = true;

  const core = fillCloud(CORE_COUNT, w, d, h);
  const sparks = fillCloud(SPARK_COUNT, w * 1.05, d * 1.05, h * 1.08);
  const coreGeo = new THREE.BufferGeometry();
  coreGeo.setAttribute('position', new THREE.BufferAttribute(core.positions, 3));
  const sparkGeo = new THREE.BufferGeometry();
  sparkGeo.setAttribute('position', new THREE.BufferAttribute(sparks.positions, 3));

  const dot = Math.max(0.6, Math.min(1.8, Math.min(w, d) * 0.014));
  const corePts = new THREE.Points(coreGeo, makeMat(0x66f0ff, 0.88, dot));
  const sparkPts = new THREE.Points(sparkGeo, makeMat(0xffe566, 0.7, dot * 0.75));
  const holo = makeHoloVolume(w, d, h);
  const ring = makeRing(Math.max(w, d) * 0.58);
  const ring2 = makeRing(Math.max(w, d) * 0.72);
  ring2.material.opacity = 0.22;
  const scan = makeScan(w, d);

  group.add(holo, ring, ring2, scan, corePts, sparkPts);
  handle.root.add(group);

  handle.placeholder = {
    group,
    coreGeo,
    sparkGeo,
    core,
    sparks,
    holo,
    ring,
    ring2,
    scan,
    h,
    start: performance.now(),
    raf: 0
  };
  handle.modelSize = { placeholder: true, width: w, depth: d, height: h };
  setMarkerHidden(handle, true);

  const loop = () => {
    if (handle.disposed || !handle.placeholder) return;
    tickLoadAssembleFx(handle);
    try {
      handle.map.triggerRepaint();
    } catch (_) { /* ignore */ }
    handle.placeholder.raf = requestAnimationFrame(loop);
  };
  handle.placeholder.raf = requestAnimationFrame(loop);
}

export function tickLoadAssembleFx(handle) {
  const fx = handle?.placeholder;
  if (!fx) return;
  const t = (performance.now() - fx.start) / 1000;
  const breathe = 0.5 + 0.42 * (0.5 + 0.5 * Math.sin(t * 1.05));
  writeCloud(fx.core.positions, fx.core.seeds, t, breathe);
  writeCloud(fx.sparks.positions, fx.sparks.seeds, t * 1.15, breathe * 0.92);
  fx.coreGeo.attributes.position.needsUpdate = true;
  fx.sparkGeo.attributes.position.needsUpdate = true;
  if (fx.holo?.material) {
    fx.holo.material.opacity = 0.22 + Math.sin(t * 2.4) * 0.12;
  }
  if (fx.ring) fx.ring.rotation.z = t * 0.7;
  if (fx.ring2) fx.ring2.rotation.z = -t * 0.45;
  if (fx.scan) {
    fx.scan.position.z = (0.08 + (0.5 + 0.5 * Math.sin(t * 1.6)) * 0.84) * fx.h;
    fx.scan.material.opacity = 0.1 + (0.5 + 0.5 * Math.sin(t * 1.6)) * 0.12;
  }
}

export function removeLoadAssembleFx(handle) {
  const fx = handle?.placeholder;
  if (!fx) return;
  if (fx.raf) {
    try {
      cancelAnimationFrame(fx.raf);
    } catch (_) { /* ignore */ }
  }
  try {
    handle.root?.remove?.(fx.group);
  } catch (_) { /* ignore */ }
  try {
    fx.group?.traverse?.((node) => {
      node.geometry?.dispose?.();
      const mats = Array.isArray(node.material) ? node.material : [node.material];
      mats.forEach((m) => m?.dispose?.());
    });
  } catch (_) { /* ignore */ }
  handle.placeholder = null;
  setMarkerHidden(handle, false);
}
